import { useTranslation } from "react-i18next";
import { translate as tr } from "../../i18n";
import React, { useEffect, useState } from "react";
import { useDispatch } from "react-redux";
import { useSelector } from "react-redux";
import { useNavigate, useParams } from "react-router-dom";
import axios from "axios";
import Sidebar from "../common/sidebar";
import Footer from "../common/footer";
import { setAlert } from "../../actions/alert";

const EMPTY_FORM = {
  client_name: "",
  company_name: "",
  billing_contact_name: "",
  billing_email: "",
  billing_phone: "",
  billing_tax_id: "",
  billing_vat_number: "",
  billing_address_line1: "",
  billing_address_line2: "",
  billing_city: "",
  billing_state: "",
  billing_postal_code: "",
  billing_country: "",
  invoice_currency: "EUR",
  payment_terms_days: "14",
  invoice_notes: "",
};

export default function ClientProfilePage() {
  useTranslation();
  const auth = useSelector((state) => state.auth);
  const dispatch = useDispatch();
  const history = useNavigate();
  const { clientId } = useParams();
  const isNew = !clientId || clientId === "new";
  const canManageClients = ["ADMIN", "MANAGER"].includes(auth.workspace_role);
  const [form, setForm] = useState(EMPTY_FORM);
  const [errors, setErrors] = useState({});
  const [saving, setSaving] = useState(false);
  const [loading, setLoading] = useState(!isNew);
  const [stats, setStats] = useState({ projectCount: 0 });

  useEffect(() => {
    if (isNew) return;
    const loadClient = async () => {
      setLoading(true);
      try {
        const config = {
          headers: {
            Authorization: `Bearer ${auth.token}`,
            "Content-Type": "application/json",
          },
        };
        const body = JSON.stringify({
          client_id: clientId,
          workspace_id: auth.workspace_id,
        });
        const res = await axios.post("/api/client/details", body, config);
        const client = res.data.client;
        setForm({
          client_name: client.client_name || "",
          company_name: client.company_name || "",
          billing_contact_name: client.billing_contact_name || "",
          billing_email: client.billing_email || "",
          billing_phone: client.billing_phone || "",
          billing_tax_id: client.billing_tax_id || "",
          billing_vat_number: client.billing_vat_number || "",
          billing_address_line1: client.billing_address_line1 || "",
          billing_address_line2: client.billing_address_line2 || "",
          billing_city: client.billing_city || "",
          billing_state: client.billing_state || "",
          billing_postal_code: client.billing_postal_code || "",
          billing_country: client.billing_country || "",
          invoice_currency: client.invoice_currency || "EUR",
          payment_terms_days: client.payment_terms_days?.toString() || "14",
          invoice_notes: client.invoice_notes || "",
        });
        setStats({ projectCount: client.projects?.length || 0 });
      } catch (err) {
        console.log(err);
      } finally {
        setLoading(false);
      }
    };
    loadClient();
  }, [auth.token, auth.workspace_id, clientId, isNew]);

  useEffect(() => {
    if (isNew && !canManageClients) {
      dispatch(setAlert("You can view clients, but only admins and managers can create them.", "warning", 5000));
      history("/clients");
    }
  }, [canManageClients, dispatch, history, isNew]);

  useEffect(() => {
    if (!isNew && !canManageClients) {
      dispatch(setAlert("Members can only view the client list.", "warning", 5000));
      history("/clients");
    }
  }, [canManageClients, dispatch, history, isNew]);

  const updateField = (key, value) => {
    setForm((prev) => ({ ...prev, [key]: value }));
    setErrors((prev) => ({ ...prev, [key]: undefined }));
  };

  const validate = () => {
    const errs = {};
    if (!form.client_name.trim()) errs.client_name = "Client name is required";
    if (form.billing_email && !/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.billing_email)) errs.billing_email = "Enter a valid billing email";
    if (form.payment_terms_days !== "" && (Number.isNaN(Number(form.payment_terms_days)) || Number(form.payment_terms_days) < 0)) errs.payment_terms_days = "Payment terms must be 0 or more";
    setErrors(errs);
    return Object.keys(errs).length === 0;
  };

  const handleSubmit = async () => {
    if (!validate()) return;
    setSaving(true);
    try {
      const config = {
        headers: {
          Authorization: `Bearer ${auth.token}`,
          "Content-Type": "application/json",
        },
      };
      const body = JSON.stringify({
        ...(isNew ? {} : { client_id: clientId }),
        ...form,
        company_name: form.company_name.trim() || form.client_name.trim(),
        workspace_id: auth.workspace_id,
      });
      const res = isNew
        ? await axios.post("/api/client/add", body, config)
        : await axios.put("/api/client/edit", body, config);
      if (res.status === 200) {
        dispatch(setAlert(isNew ? "Client created successfully" : "Client updated successfully", "success"));
        history("/clients");
      }
    } catch (err) {
      const message = err.response?.data?.errors?.[0]?.msg || err.response?.data?.msg || "Could not save client. Please try again.";
      dispatch(setAlert(message, "danger", 5000));
    } finally {
      setSaving(false);
    }
  };

  const readinessChecks = [
    !!form.billing_contact_name.trim(),
    !!form.billing_email.trim(),
    !!form.billing_address_line1.trim(),
    !!form.billing_city.trim(),
    !!form.billing_postal_code.trim(),
    !!form.billing_country.trim(),
  ];
  const readinessScore = readinessChecks.filter(Boolean).length;

  const renderInput = (key, label, placeholder, options = {}) => (
    <div className={`pm-field${options.wide ? " pm-field--wide" : ""}`}>
      <label className="pm-label">{label}</label>
      {options.type === "textarea" ? (
        <textarea
          className="pm-input pm-input--textarea"
          placeholder={placeholder}
          value={form[key]}
          disabled={!canManageClients}
          onChange={(e) => updateField(key, e.target.value)}
          rows={options.rows || 4}
        />
      ) : options.type === "select" ? (
        <select className="pm-input" value={form[key]} disabled={!canManageClients} onChange={(e) => updateField(key, e.target.value)}>
          {options.items.map((item) => (
            <option key={item.value} value={item.value}>{item.label}</option>
          ))}
        </select>
      ) : (
        <input
          className="pm-input"
          type={options.type || "text"}
          placeholder={placeholder}
          value={form[key]}
          disabled={!canManageClients}
          onChange={(e) => updateField(key, e.target.value)}
        />
      )}
      {errors[key] && <span className="pm-error">{tr(errors[key])}</span>}
    </div>
  );

  return (
    <div>
      <Sidebar />
      <div className='content-wrap' id='wrapper'>
        <div className='main'>
          <div className='cp-shell'>
            <div className='cp-topbar'>
              <button type='button' className='cp-back' onClick={() => history("/clients")}>{tr("Back to Clients")}</button>
              <div className='cp-actions'>
                <button type='button' className='cp-btn cp-btn--secondary' onClick={() => history("/clients")}>{tr("Cancel")}</button>
                {canManageClients && (
                  <button type='button' className='cp-btn cp-btn--primary' onClick={handleSubmit} disabled={saving || loading}>
                    {saving ? tr("Saving...") : isNew ? tr("Create client") : tr("Save client")}
                  </button>
                )}
              </div>
            </div>

            <div className='cp-hero'>
              <div>
                <div className='cp-eyebrow'>{tr("Client Billing Profile")}</div>
                <h1>{isNew ? tr("Create a new invoice-ready client") : form.client_name || tr("Edit client profile")}</h1>
                <p>{canManageClients ? tr("Build the billing foundation for professional invoices, payment terms, and finance operations.") : tr("Review the billing foundation used for professional invoices, payment terms, and finance operations.")}</p>
              </div>
              <div className='cp-summary-card'>
                <span className='pm-readiness-label'>{tr("Invoice readiness")}</span>
                <strong className='pm-readiness-value'>{readinessScore}{tr("/6 fields complete")}</strong>
                <div className='pm-readiness-bar'><span style={{ width: `${(readinessScore / 6) * 100}%` }} /></div>
                <div className='cp-summary-meta'>{tr("innerLinkedProjects", { count: stats.projectCount })}</div>
              </div>
            </div>

            {loading ? (
              <div className='cp-loading-card'>{tr("Loading client profile...")}</div>
            ) : (
              <div className='cp-grid'>
                <div className='cp-main'>
                  {!canManageClients && (
                    <div className='cp-loading-card'>{tr("You have read-only access. Only workspace admins and managers can edit client billing profiles.")} </div>
                  )}
                  <div className='pm-section'>
                    <div className='pm-section-head'>
                      <h4>{tr("Business Identity")}</h4>
                      <p>{tr("How this client appears across work, billing, and future invoice PDFs.")}</p>
                    </div>
                    <div className='pm-grid pm-grid--2'>
                      {renderInput("client_name", tr("Client name"), tr("e.g. Acme Growth Team"))}
                      {renderInput("company_name", tr("Company / legal entity"), tr("e.g. Acme GmbH"))}
                    </div>
                  </div>

                  <div className='pm-section'>
                    <div className='pm-section-head'>
                      <h4>{tr("Billing Contact")}</h4>
                      <p>{tr("The person and channel your invoices should reliably reach.")}</p>
                    </div>
                    <div className='pm-grid pm-grid--3'>
                      {renderInput("billing_contact_name", tr("Contact name"), tr("e.g. Maria Keller"))}
                      {renderInput("billing_email", tr("Billing email"), "billing@client.com", { type: "email" })}
                      {renderInput("billing_phone", tr("Phone"), tr("e.g. +49 30 1234 5678"), { type: "tel" })}
                    </div>
                  </div>

                  <div className='pm-section'>
                    <div className='pm-section-head'>
                      <h4>{tr("Billing Address")}</h4>
                      <p>{tr("Used for invoice headers, compliance, and cross-border documentation.")}</p>
                    </div>
                    <div className='pm-grid pm-grid--2'>
                      {renderInput("billing_address_line1", tr("Address line 1"), tr("Street address and number"))}
                      {renderInput("billing_address_line2", tr("Address line 2"), tr("Suite, floor, department"))}
                      {renderInput("billing_city", tr("City"), tr("e.g. Berlin"))}
                      {renderInput("billing_state", tr("State / region"), tr("e.g. Berlin"))}
                      {renderInput("billing_postal_code", tr("Postal code"), tr("e.g. 10115"))}
                      {renderInput("billing_country", tr("Country"), tr("e.g. Germany"))}
                    </div>
                  </div>

                  <div className='pm-section'>
                    <div className='pm-section-head'>
                      <h4>{tr("Invoice Defaults")}</h4>
                      <p>{tr("Save the financial defaults your team will reuse when invoice generation is added.")}</p>
                    </div>
                    <div className='pm-grid pm-grid--3'>
                      {renderInput("invoice_currency", tr("Currency"), "", {
                        type: "select",
                        items: [
                          { value: "EUR", label: "EUR" },
                          { value: "USD", label: "USD" },
                          { value: "GBP", label: "GBP" },
                          { value: "CHF", label: "CHF" },
                        ],
                      })}
                      {renderInput("payment_terms_days", tr("Payment terms (days)"), "14", { type: "number" })}
                      {renderInput("billing_tax_id", tr("Tax ID"), tr("Company tax identifier"))}
                      {renderInput("billing_vat_number", tr("VAT number"), tr("EU VAT number if applicable"))}
                      {renderInput("invoice_notes", tr("Invoice notes"), tr("Optional footer text, remittance details, or finance notes."), { type: "textarea", wide: true })}
                    </div>
                  </div>
                </div>

                <div className='cp-side'>
                  <div className='cp-side-card'>
                    <h3>{tr("What this page prepares")}</h3>
                    <p>{tr("We’re setting up the core billing profile so invoice generation can be introduced cleanly for freelancers and teams.")}</p>
                    <ul className='cp-side-list'>
                      <li>{tr("Invoice recipient and contact details")}</li>
                      <li>{tr("Legal billing address")}</li>
                      <li>{tr("VAT / tax references")}</li>
                      <li>{tr("Currency and payment term defaults")}</li>
                    </ul>
                  </div>
                </div>
              </div>
            )}
          </div>
        </div>
      </div>
      <Footer />
    </div>
  );
}
